import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { initialReducerState, errorMessage } from './constants/index'
import { reducer, useMultiplyRefs, getSetByProp, getSelectOptions } from './utils'
import { IMaskInput } from 'react-imask'
import CalculatorSelect from './components/calculatorSelect'
import { debounce } from '../../js/utils/debounce'
import { makeRequest } from '../../js/utils/makeRequest'
import { getFormDataFromObj } from '../../js/utils/getFormDataFromObj'
import { isEmptyObj } from '../../js/utils/isEmptyObj'

const App = ({ data, rap, initialTabs, rapnet, claritySort = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'SI3', 'I1', 'I2', 'I3'], urlSaveValues, urlGetValues, auxParams, initialValues = {} }) => {
  const [state, dispatch] = useReducer(reducer, initialReducerState)
  const [compareValues, setCompareValues] = useState([])
  const [savedData, setSavedData] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const refCarat = useRef(null)
  const refBody = useRef(null)

  const setSavedValues = () => {
    setIsLoading(true)
    const savedValues = async () => {
      return makeRequest({ url: urlGetValues, data: getFormDataFromObj(auxParams), method: 'POST' })
        .then(({ success, data, errors }) => {
          if (success) {
            return data
          } else {
            return Promise.reject(errors)
          }
        })
    }
    savedValues()
      .then((data) => {
        setSavedData(data)
        const { carat, isRound, color, clarity, rap } = data
        setInitialValues(carat, isRound, color, clarity, rap)
      })
      .catch((e) => {
        console.error(e)
        setSavedData({
          carat: state.carat,
          isRound: state.isRound,
          color: state.color,
          clarity: state.clarity,
          rap: state.rap
        })
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  // Простановка предвыбранных значений в калькуляторе
  const setInitialValues = (carat, isRound, color, clarity, rap) => {
    if (carat) {
      dispatch({ type: 'carat', payload: carat })
    }
    selectRefs.forEach(({ current }) => {
      if (current.props.name === 'shape' && isRound !== undefined) {
        current.setValue(current.props.options.find((option) => option.isRound === isRound))
      } else if (current.props.name === 'color' && color) {
        current.setValue(current.props.options.find((option) => option.value === color))
      } else if (current.props.name === 'clarity' && clarity) {
        current.setValue(current.props.options.find((option) => option.value === clarity))
      }
    })
    if (rap) {
      dispatch({ type: 'rap', payload: rap.replace(',', '.') })
    }
  }

  const callData = useMemo(() => {
    return {
      colors: getSetByProp(data, 'color'),
      clarity: getSetByProp(data, 'clarity').sort((a, b) => claritySort.indexOf(a) - claritySort.indexOf(b))
    }
  }, [])

  const onSelectChange = (type, value) => {
    dispatch({ type, payload: value })
  }

  const findClosest = (arr, num) => {
    if (arr == null) {
      return
    }

    let closest = arr[0]
    for (const item of arr) {
      if (Math.abs(item - num) < Math.abs(closest - num)) {
        closest = item
      }
    }
    return closest
  }

  const reset = () => {
    setCompareValues([])
    dispatch({ type: 'resetAllInputs' })
    dispatch({ type: 'rap', payload: rap.initialValue })
    selectRefs.forEach(({ current }) => {
      if (current.props.name === 'shape') {
        current.setValue(current.props.options.find((option) => option.isActive))
      } else {
        current.setValue(current.props.options[0])
      }
    })
    refCarat.current.element.focus()
  }

  // Поиск значения цены по введённым в калькуляторе параметрам.
  useEffect(() => {
    if (state.carat.length) {
      const find = data.filter((item) => {
        return (item.color === state.color && item.clarity === state.clarity && item.is_round === state.shape)
      })
      const minMaxValues = [...new Set(find.map(item => item.min)), ...new Set(find.map(item => item.max))]
      const closestValue = findClosest(minMaxValues, +state.carat.replace(',', '.'))
      const obj = find.filter((item) => item.min === closestValue || item.max === closestValue)
      if (Number(state.carat.replace(',', '.'))) {
        dispatch({ type: 'price', payload: +obj[0].price + +(obj[0].price / 100) * state.rap })
        dispatch({ type: 'priceInitial', payload: +obj[0].price })
      } else {
        dispatch({ type: 'price', payload: 0 })
        dispatch({ type: 'priceInitial', payload: 0 })
      }
    } else {
      dispatch({ type: 'resetAllInputs' })
    }
  }, [state.carat, state.color, state.clarity, state.shape, state.rap])

  useEffect(() => {
    if (state.activeInputName !== 'priceTotal') {
      dispatch({ type: 'priceTotal', payload: Math.round(state.carat.replace(',', '.') * state.price.toString().replace(',', '.')) })
    }
    dispatch({ type: 'priceTotalInitial', payload: Math.round(state.carat.replace(',', '.') * state.priceInitial.toString().replace(',', '.')) })
  }, [state.price, state.carat])

  useEffect(() => {
    if (state.activeInputName !== 'carat' && state.priceInitial !== '0' && state.priceTotalInitial !== '0') {
      const rap = (Number(String(state.price).replace(',', '.')) * 100 / state.priceInitial) - 100

      const decimal = rap % 1 === 0 ? 0 : 1
      const roundedRap = Number((Math.round(rap * 2) / 2).toFixed(decimal))

      dispatch({ type: 'rap', payload: roundedRap })
    }
  }, [state.price, state.priceTotal])

  useEffect(() => {
    if (state.activeInputName === 'priceTotal') {
      if (Number(state.priceTotal) !== 0 && Number(state.carat) !== 0) {
        dispatch({ type: 'price', payload: Math.round(state.priceTotal.toString().replace(',', '.') / state.carat.replace(',', '.')) })
      }
    }
  }, [state.priceTotal])

  const saveValues = () => {
    if (state.price) {
      setCompareValues((prevState) => [{ price: state.price, priceTotal: state.priceTotal }, ...prevState])
    }
    refCarat.current.element.focus()
  }

  useEffect(() => {
    dispatch({ type: 'rap', payload: rap.initialValue })
  }, [])

  const selects = useMemo(() => {
    return [
      {
        values: getSelectOptions(initialTabs, true),
        title: 'Форма',
        group: 'shape',
        initialOption: getSelectOptions(initialTabs, true).find(tab => tab.isActive)
      },
      {
        values: getSelectOptions(callData.colors),
        title: 'Цвет',
        group: 'color'
      },
      {
        values: getSelectOptions(callData.clarity),
        title: 'Чистота',
        group: 'clarity'
      }
    ]
  }, [])

  const selectRefs = useMultiplyRefs(3)

  const handleInputFocus = (e) => {
    dispatch({ type: 'activeInputName', payload: e.target.name })
  }

  const handleRapInput = (e) => {
    if (e.target.value > 100) {
      e.target.value = '100'
    }
    if (e.target.value < -100) {
      e.target.value = '-100'
    }
    dispatch({ type: 'rap', payload: e.target.value })
  }

  // Сохранение и отправка значений, при изменении значений калькулятора.
  const saveFilter = () => {
    const body = {
      ...auxParams,
      ...refBody.current
    }
    // На локале работает только с get
    // makeRequest({ url: urlSaveValues, data: getFormDataFromObj(body), method: 'GET' })
    makeRequest({ url: urlSaveValues, data: getFormDataFromObj(body), method: 'POST' })
      .then(({ success, error = errorMessage }) => {
        if (!success) {
          return Promise.reject(error)
        }
      })
      .catch((e) => {
        console.error(e)
      })
  }

  const saveFilterDebounce = useCallback(debounce(saveFilter, 3000), [])

  useEffect(() => {
    if (
      !isLoading &&
      Object.keys(savedData).length &&
      (
        state.color !== savedData.color ||
        state.clarity !== savedData.clarity ||
        state.shape !== savedData.shape ||
        state.rap !== savedData.rap ||
        state.carat !== savedData.carat
      )
    ) {
      refBody.current = {
        color: state.color,
        clarity: state.clarity,
        shape: state.shape,
        rap: state.rap,
        carat: state.carat
      }
      saveFilterDebounce()
    }
  }, [state.color, state.clarity, state.shape, state.rap, state.carat])

  // Запрос при первой загрузке на получение сохранённых данных - опций, которые были введены в последний раз перед закрытием калькулятора.
  useEffect(() => {
    refCarat.current.element.focus()
    if (auxParams && urlGetValues && isEmptyObj(initialValues)) {
      setSavedValues()
    }
    if (!isEmptyObj(initialValues)) {
      setSavedData(initialValues)
      const { carat, isRound, color, clarity, rap } = initialValues
      setInitialValues(carat, isRound, color, clarity, rap)
    }
  }, [])

  return (
    <div className={`${rapnet ? 'rapnet' : ''} modal-calculator`} >
      <h2 className='h4 modal-calculator__title'>Ваш бриллиант</h2>
      <div className='modal-calculator__row'>
        <label className='modal-calculator__input-item'>
          <span className='modal-calculator__input-label'>Размер</span>
          <IMaskInput
            mask={Number}
            radix=','
            mapToRadix={['.']}
            min={0}
            value={state.carat}
            onAccept={
              (value) => dispatch({ type: 'carat', payload: value })
            }
            className='modal-calculator__carat modal-calculator__input'
            ref={refCarat}
            onFocus={handleInputFocus}
            name={'carat'}
          />
        </label>

        {selects.map((select, index) => {
          return <CalculatorSelect
            key={index}
            {...select}
            onControlChange={onSelectChange}
            ref={selectRefs[index]}
          />
        })}
      </div>
      <div className='modal-calculator__row modal-calculator__row--prices'>
        <div className='modal-calculator__prices modal-calculator__prices--rap'>
          <p className='modal-calculator__input-label'>Rap price</p>
          <p className='modal-calculator__input-item'>
            <span className='modal-calculator__input-label'>CT</span>
            <span className='modal-calculator__input-value'>$ {state.priceInitial.toString().replace('.', ',')}</span>
          </p>
          <p className='modal-calculator__input-item'>
            <span className='modal-calculator__input-label'>Общая</span>
            <span className='modal-calculator__input-value'>$ {state.priceTotalInitial.toString().replace('.', ',')}</span>
          </p>
        </div>
        <div className='modal-calculator__prices'>
          <p className='modal-calculator__input-label'>Ваша цена</p>
          <label className='modal-calculator__input-item'>
            <span className='modal-calculator__input-label'>CT</span>
            <IMaskInput
              mask={Number}
              radix=','
              mapToRadix={['.']}
              min={0}
              value={state.price.toString()}
              onAccept={
                (value) => dispatch({ type: 'price', payload: value })
              }
              className='modal-calculator__price-count modal-calculator__input'
              onFocus={handleInputFocus}
              name={'price'}
            />
          </label>
          <label className='modal-calculator__input-item'>
            <span className='modal-calculator__input-label'>Rap%</span>
            <input
              type='number'
              className='modal-calculator__input modal-calculator__rap-count'
              min={rap.minValue}
              max={rap.maxValue}
              step={rap.step}
              placeholder={rap.initialValue}
              value={state.rap.toString()}
              onFocus={handleInputFocus}
              name={'rap'}
              onChange={handleRapInput}
            />
          </label>
          <label className='modal-calculator__input-item'>
            <span className='modal-calculator__input-label'>Общая</span>
            <IMaskInput
              mask={Number}
              radix=','
              mapToRadix={['.']}
              min={0}
              value={state.priceTotal.toString()}
              onAccept={
                (value) => dispatch({ type: 'priceTotal', payload: value })
              }
              className='modal-calculator__price-count modal-calculator__input'
              onFocus={handleInputFocus}
              name={'priceTotal'}
            />
          </label>
        </div>
      </div>
      <div className='modal-calculator__row modal-calculator__control'>
        <button onClick={saveValues} className='modal-calculator__btn modal-calculator__btn_compare'>Сравнить</button>
        <button onClick={reset} className='modal-calculator__btn modal-calculator__btn_reset'>Сбросить</button>
      </div>
      <div>
        {compareValues.length ? compareValues.map((item, index) => {
          return <p className='modal-calculator__price' key={index}>{item.price.toString().replace('.', ',')}$ | {item.priceTotal.toString().replace('.', ',')}$</p>
        }) : ''}
      </div>
    </div>
  )
}

export default App
